import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Stack,
  Snackbar,
  Alert,
  Chip,
  Avatar,
  Fade,
  Zoom,
  Divider,
} from '@mui/material';
import DiamondIcon from '@mui/icons-material/Diamond';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import SendIcon from '@mui/icons-material/Send';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SecurityIcon from '@mui/icons-material/Security';

// Premium Color Palette
const colors = {
  navy: '#0a1128',
  darkNavy: '#060b1f',
  gold: '#c9a03d',
  lightGold: '#d4af37',
  darkGold: '#a0782c',
  charcoal: '#1a1a2e',
  lightCharcoal: '#16213e',
  pearl: '#f5f5f0',
  darkPearl: '#e8e8e0',
  white: '#ffffff',
  textDark: '#1a1a2e',
  textLight: '#6b6b6b',
  premiumDark: '#0d1117',
  premiumGold: '#b8942e',
};

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setSnackbar({ open: true, message: 'Thank you! Our concierge team will respond within 24 hours.', severity: 'success' });
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Box>
      {/* Hero Section - Increased Height with Reduced Overlay Opacity */}
      <Box 
        sx={{ 
          position: 'relative',
          py: { xs: 12, md: 16 },
          minHeight: { xs: 450, md: 550 },
          color: 'white', 
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          backgroundImage: 'url("/images/productspageherobg.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(13,17,23,0.4) 0%, rgba(26,26,46,0.3) 100%)',
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={1000}>
            <Box>
              <Avatar 
                src="/logo/logo.png" 
                alt="Diamond Card"
                sx={{ 
                  width: 120, 
                  height: 120, 
                  mx: 'auto', 
                  mb: 3,
                  backgroundColor: 'transparent',
                  '& img': { objectFit: 'contain' }
                }}
                variant="square"
              />
              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '2rem', md: '3.5rem' }, 
                  mb: 2, 
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${colors.premiumGold} 0%, ${colors.lightGold} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  letterSpacing: -0.5,
                }}
              >
                YOUR OWN PRIVATE BANK
              </Typography>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontSize: { xs: '1.5rem', md: '2.5rem' }, 
                  color: colors.premiumGold, 
                  fontWeight: 700,
                  letterSpacing: 2,
                }}
              >
                THE SAFE BANK ®
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Contact Form and Info */}
        <Grid container spacing={5}>
          {/* Contact Form */}
          <Grid item xs={12} md={7}>
            <Zoom in timeout={500}>
              <Paper elevation={0} sx={{ p: 4.5, borderRadius: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: colors.navy, mb: 2, fontSize: { xs: '1.6rem', md: '2rem' } }}>
                  GET IN TOUCH
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: colors.textLight }}>
                  Our concierge team will respond to your inquiry within 24 hours
                </Typography>
                
                <form onSubmit={handleContactSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Full Name*"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        sx={{ 
                          '& .MuiOutlinedInput-root': { borderRadius: 2 },
                          '& .MuiOutlinedInput-root:hover fieldset': { borderColor: colors.premiumGold },
                          '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: colors.premiumGold },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email Address*"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        sx={{ 
                          '& .MuiOutlinedInput-root': { borderRadius: 2 },
                          '& .MuiOutlinedInput-root:hover fieldset': { borderColor: colors.premiumGold },
                          '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: colors.premiumGold },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        variant="outlined"
                        sx={{ 
                          '& .MuiOutlinedInput-root': { borderRadius: 2 },
                          '& .MuiOutlinedInput-root:hover fieldset': { borderColor: colors.premiumGold },
                          '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: colors.premiumGold },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Your Message"
                        name="message"
                        multiline
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        variant="outlined"
                        sx={{ 
                          '& .MuiOutlinedInput-root': { borderRadius: 2 },
                          '& .MuiOutlinedInput-root:hover fieldset': { borderColor: colors.premiumGold },
                          '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: colors.premiumGold },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        endIcon={<SendIcon />}
                        sx={{ 
                          bgcolor: colors.premiumGold,
                          color: colors.navy,
                          fontWeight: 700, 
                          px: 5,
                          py: 1.5,
                          borderRadius: 3,
                          '&:hover': { bgcolor: colors.lightGold, transform: 'translateY(-2px)' },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Send Message
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Zoom>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              <Zoom in timeout={700}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 4, bgcolor: colors.navy, color: 'white' }}>
                  <Typography variant="h5" gutterBottom sx={{ color: colors.premiumGold, fontWeight: 700, fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                    Contact Information
                  </Typography>
                  <Stack spacing={2.5} sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: colors.premiumGold, width: 45, height: 45, mr: 2 }}>
                        <PhoneIcon sx={{ color: colors.navy }} />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Phone (US)</Typography>
                        <Typography variant="body1" fontWeight="bold">+1-302-295-3810</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: colors.premiumGold, width: 45, height: 45, mr: 2 }}>
                        <PhoneIcon sx={{ color: colors.navy }} />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Phone (South Africa)</Typography>
                        <Typography variant="body1" fontWeight="bold">+27 72 010 0332</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: colors.premiumGold, width: 45, height: 45, mr: 2 }}>
                        <EmailIcon sx={{ color: colors.navy }} />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Email</Typography>
                        <Typography variant="body1" fontWeight="bold">info@diamondcard.com</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: colors.premiumGold, width: 45, height: 45, mr: 2 }}>
                        <LanguageIcon sx={{ color: colors.navy }} />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Website</Typography>
                        <Typography variant="body1" fontWeight="bold">www.diamondcard.com</Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccessTimeIcon sx={{ color: colors.premiumGold, fontSize: 28, mr: 2 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Support Hours</Typography>
                        <Typography variant="body1" fontWeight="bold">24/7 Concierge Service</Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Paper>
              </Zoom>

              <Zoom in timeout={900}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.05)', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 15px 30px ${colors.premiumGold}20` } }}>
                  <Typography variant="h5" gutterBottom sx={{ color: colors.navy, fontWeight: 700, fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                    Office Locations
                  </Typography>
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ color: colors.premiumGold, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon sx={{ fontSize: 16 }} /> United States
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, pl: 3 }}>
                        Suite 1200 1000 N. West Street,<br />
                        Downtown Wilmington,<br />
                        Delaware, 19801
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ color: colors.premiumGold, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon sx={{ fontSize: 16 }} /> South Africa
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, pl: 3 }}>
                        341 Beyers Naude Drive,<br />
                        Northcliff Johannesburg Drive,<br />
                        South Africa, 2195
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Zoom>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Security Assurance Bar */}
      <Box sx={{ bgcolor: colors.pearl, py: 4 }}>
        <Container>
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid item>
              <Chip 
                icon={<SecurityIcon />} 
                label="256-bit SSL Encrypted" 
                sx={{ 
                  bgcolor: 'white', 
                  fontWeight: 600,
                  '&:hover': { bgcolor: colors.premiumGold, color: colors.navy, transform: 'translateY(-2px)' },
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                }} 
              />
            </Grid>
            <Grid item>
              <Chip 
                icon={<AccessTimeIcon />} 
                label="24/7 Concierge Support" 
                sx={{ 
                  bgcolor: 'white', 
                  fontWeight: 600,
                  '&:hover': { bgcolor: colors.premiumGold, color: colors.navy, transform: 'translateY(-2px)' },
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                }} 
              />
            </Grid>
            <Grid item>
              <Chip 
                icon={<DiamondIcon />} 
                label="Global Network" 
                sx={{ 
                  bgcolor: 'white', 
                  fontWeight: 600,
                  '&:hover': { bgcolor: colors.premiumGold, color: colors.navy, transform: 'translateY(-2px)' },
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                }} 
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Diamond Network Footer */}
      <Box sx={{ bgcolor: colors.premiumGold, py: 3.5, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: colors.navy, letterSpacing: 4, fontSize: { xs: '1.3rem', md: '1.8rem' } }}>
          DIAMOND NETWORK
        </Typography>
        <Typography variant="caption" sx={{ color: colors.navy, opacity: 0.7, mt: 0.5, display: 'block', letterSpacing: 1.5 }}>
          Connecting global financial excellence
        </Typography>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ bgcolor: colors.navy, color: colors.premiumGold, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;